/**
 * This is loaded by ./preload.js
 */
import 'virtual:windi.css' // eslint-disable-line import/no-unresolved
import 'virtual:windi-devtools' // eslint-disable-line import/no-unresolved
import '@monogrid/vue-boost/core/styles.css'
import 'swiper/css'
import 'swiper/css/grid'
import 'what-input'
import { createApp } from 'vue'
import { createHead } from '@vueuse/head'
import VueGtag, { trackRouter } from 'vue-gtag-next'
import { setupRouter, routes } from './router.js'
import {
  cmsAuthInjection,
  getTranslationsInjection,
  useI18nInjection,
  setEnv,
  setGlobalFeatures
} from '@monogrid/vue-boost'

import App from './App.vue'
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

const { USE_CMS, USE_I18N } = setGlobalFeatures({
  cms: false,
  i18n: true
})
export const env = DOT_ENV
setEnv(env)

if (window.app && typeof window.app.unmount === 'function') window.app.unmount()

/**
  * @type {import('node_modules/@vue/runtime-core/dist/runtime-core').InjectionKey<import('./Preloader.vue').default>}
  */
export const preloaderInjection = Symbol('preloader')

/**
  * This function loads all optional modules of this boilerplate
  *
  * @returns all the optional dependencies
  */
const loadOptionalModules = async () => {
  const returnObject = {}

  if (USE_CMS) {
    // I choose to make these separate chunks
    const loadCMSClient = () => import('./utils/cms-bootstrap')
    // // we load them
    const { CMSAuth, CMSAuthPlugin, provideCMSClient } = await loadCMSClient()

    // // we bootstrap cognito authentication
    const cognitoUser = await CMSAuth.bootstrap(env)

    returnObject.cmsClientProvider = provideCMSClient(env, CMSAuth)
    returnObject.cognitoUser = cognitoUser
    returnObject.CMSAuthPlugin = CMSAuthPlugin
    returnObject.CMSAuth = CMSAuth
  }

  if (USE_I18N) {
    const loadTranslations = () => import('../config/translations/index.js')
    const loadI18N = () => import('@monogrid/vue-boost/i18n')
    const { setupI18n, useLocalizedRoutes, addRouterHooks, i18n, setTranslations, getTranslations } = await loadI18N()
    const translations = await loadTranslations()
    setTranslations(translations.default)
    returnObject.useI18n = i18n
    returnObject.setupI18n = setupI18n
    returnObject.setTranslations = setTranslations
    returnObject.getTranslations = getTranslations
    returnObject.useLocalizedRoutes = useLocalizedRoutes
    returnObject.addRouterHooks = addRouterHooks
  }

  return returnObject
}

/**
  * Load the optional modules
  */
loadOptionalModules().then(({ cmsClientProvider, cognitoUser, CMSAuthPlugin, CMSAuth, setupI18n, useLocalizedRoutes, getTranslations, addRouterHooks, useI18n }) => {
  const app = createApp(App)
  // https://github.com/vueuse/head
    .use(createHead())
  // https://matteo-gabriele.gitbook.io/vue-gtag/v/next/
    .use(VueGtag)
    .provide(preloaderInjection, window.preloaderInstance) // TODO: Fix this horrible hack

  // add optional internationalization support
  if (USE_I18N) {
    app.use(setupI18n())
    app.provide(useI18nInjection, useI18n)
    app.provide(getTranslationsInjection, getTranslations)
  }

  // add optional CMS support
  if (USE_CMS) {
    const { client, urql } = cmsClientProvider
    app.use(urql, client)
    app.use(CMSAuthPlugin, { graphQLClient: app._context.provides.$urql.value })
    app.provide(cmsAuthInjection, CMSAuth)
  }

  // https://next.router.vuejs.org/guide/
  const router = setupRouter(
    USE_I18N ? useLocalizedRoutes(routes) : routes,
    {
      useI18n,
      cognitoUser,
      addRouterHooks
    }
  )

  // https://next.router.vuejs.org/guide/
  app.use(router)

  // https://matteo-gabriele.gitbook.io/vue-gtag/v/next/router-tracking
  trackRouter(router)

  app.mount('#app')
  window.app = app
  return null
}).catch(error => console.error(error))
